import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import {
  useTSQInvPositionList,
  useTSMDeactivateInvPosition,
  useTSMReactivateInvPosition,
} from "../../hooks/useTSInvPosition";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { InvPosFull } from "../../types/invPosTypes";
import { paginationDefaults } from "../../config/settingsDefaults";

export const PageInvPosList: React.FC = () => {
  const navigate = useNavigate();
  const [positions, setPositions] = useState<InvPosFull[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);
  const [orderBy, setOrderBy] = useState<string>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const {
    data: posResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQInvPositionList({
    skip: -1,
    limit: -1,
    active_status: statusFilter,
    filters: [[{ field: "quantity_on_hand", operator: ">", value: 0 }]],
  });

  const deactivatePosMutation = useTSMDeactivateInvPosition();
  const reactivatePosMutation = useTSMReactivateInvPosition();

  useEffect(() => {
    if (posResponse?.items) {
      setPositions(posResponse.items);
      setTotalCount(posResponse.total);
    }
  }, [posResponse]);

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this Position?")) {
      return;
    }

    try {
      await deactivatePosMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate Position:", err);
      setError(err.response?.data?.detail || "Failed to deactivate Position");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivatePosMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate Position:", err);
      setError(err.response?.data?.detail || "Failed to reactivate Position");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (pos: InvPosFull, property: string): any => {
    switch (property) {
      case "lot_num":
        return pos.inventory_lot.lot_number.toLowerCase() || "";
      case "item_num":
        return pos.inventory_lot.item.number.toLowerCase() || "";
      case "status":
        return pos.status.name.toLowerCase() || "";
      case "is_available":
        return pos.status.allow_picking;
      case "quantity_on_hand":
        return parseFloat(pos.quantity_on_hand) || 0;
      case "last_movement_date":
        return new Date(pos.last_movement_date || "");
      default:
        return "";
    }
  };

  // Note: Inventory statuses are configuration data and typically don't support delete/restore operations
  // They are managed through the backend configuration and should remain stable for operational integrity

  const filteredAndSortedPositions = useMemo(() => {
    const filtered = positions.filter((pos) => {
      const matchesSearch =
        searchTerm === "" ||
        pos.status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pos.inventory_lot.lot_number
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        pos.inventory_lot.item.number
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [positions, searchTerm, orderBy, order, pagination]);

  if (isLoading && positions.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Inventory Balances
        </Typography>
        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/inventory/balances/form?mode=create")}
        >
          Create Position
        </Button> */}
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search balances by status name or lot/item number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "lot_num" ? order : false}>
                <TableSortLabel
                  active={orderBy === "lot_num"}
                  direction={orderBy === "lot_num" ? order : "asc"}
                  onClick={() => handleSort("lot_num")}
                >
                  Lot Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "item_num" ? order : false}>
                <TableSortLabel
                  active={orderBy === "item_num"}
                  direction={orderBy === "item_num" ? order : "asc"}
                  onClick={() => handleSort("item_num")}
                >
                  Item Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "is_available" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "is_available"}
                  direction={orderBy === "is_available" ? order : "asc"}
                  onClick={() => handleSort("is_available")}
                >
                  Is Available?
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "quantity_on_hand" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "quantity_on_hand"}
                  direction={orderBy === "quantity_on_hand" ? order : "asc"}
                  onClick={() => handleSort("quantity_on_hand")}
                >
                  Quantity On Hand
                </TableSortLabel>
              </TableCell>
              <TableCell>UOM</TableCell>
              <TableCell
                sortDirection={orderBy === "last_movement_date" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "last_movement_date"}
                  direction={orderBy === "last_movement_date" ? order : "asc"}
                  onClick={() => handleSort("last_movement_date")}
                >
                  Last Movement Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && positions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedPositions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {searchTerm
                      ? "No balances found matching your search."
                      : "No inventory balances found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedPositions.map((position) => (
                <TableRow key={position.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {position.inventory_lot.lot_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {position.inventory_lot.item.number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {position.status.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {position.status.allow_picking ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {position.status.allow_picking ? "Yes" : "No"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(
                      parseFloat(position.quantity_on_hand) /
                      parseFloat(
                        position.inventory_lot.item.storage_uom
                          .conversion_factor,
                      )
                    ).toFixed(3)}
                  </TableCell>
                  <TableCell>
                    {position.inventory_lot.item.storage_uom.unit_symbol}
                  </TableCell>
                  <TableCell>
                    {position.last_movement_date &&
                      new Date(
                        position.last_movement_date,
                      ).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {/* <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/inventory/position/form?mode=view&id=${position.id}`
                            )
                          }
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Position">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/inventory/position/form?mode=edit&id=${position.id}`
                            )
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                      {!position.is_record_active ? (
                        <Tooltip title="Reactivate Position">
                          <IconButton
                            size="small"
                            onClick={() => handleReactivate(position.id)}
                            color="success"
                            disabled={reactivatePosMutation.isPending}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Deactivate Position">
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivate(position.id)}
                            color="error"
                            disabled={deactivatePosMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
